import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SchoolNotificationKind, SchoolNotificationRecord } from '@core/models/domain/school-notification.domain';
import { SchoolNotificationService } from './school-notification.service';
import { convertZonedDateTimeToDate } from '@core/utils/utils';

export type { SchoolNotificationKind } from '@core/models/domain/school-notification.domain';

/** One entry of the in-app notification list, ready to render and to open. */
export interface SchoolNotification {
  id: string;
  kind: SchoolNotificationKind;
  title: string;
  message: string;
  date: Date | undefined;
  read: boolean;
  /** Router commands of the page that shows this item. */
  link: string[];
  /** Optional URL fragment identifying the item on that page, so it can be highlighted. */
  fragment?: string;
}

export type NotificationPeriodKey = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'older';

/** Notifications of one time period, for the Notifications page inbox. */
export interface NotificationPeriodGroup {
  key: NotificationPeriodKey;
  label: string;
  notifications: SchoolNotification[];
}

export const PUPIL_LINK_REQUESTS_ROUTE = '/settings/admin/parent-requests/pupil-links';
export const DEACTIVATION_REQUESTS_ROUTE = '/settings/admin/parent-requests/deactivations';
export const NOTIFICATIONS_PAGE_ROUTE = '/dashboards/notifications';
export const MIS_INTEGRATION_ROUTE = '/settings/admin/mis-integration';

const MAX_BELL_NOTIFICATIONS = 20;
export const NOTIFICATION_POLL_INTERVAL_MS = 60_000;

/**
 * The school's notification inbox as this user sees it.
 *
 * The notices live in the backend, so they survive a refresh and stay after the request behind
 * them has been decided. Read state and clearing are per user: the backend keeps one inbox record
 * per account, and nothing this user does here is visible to their colleagues.
 *
 * Every write re-reads the list rather than trusting the local copy, so the page and the bell
 * always show what the server holds. The optimistic update before the call is only there to keep
 * the click responsive.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly notificationsSubject = new BehaviorSubject<SchoolNotification[]>([]);
  private readonly loadedSubject = new BehaviorSubject<boolean>(false);
  /** Records the last read left out for lack of wording (anonymised); still marked and cleared on the server. */
  private hiddenCount = 0;

  /** Every notification this user has not cleared, newest first. */
  readonly notifications$ = this.notificationsSubject.asObservable();
  /** False until the first load answers, so the page can tell "nothing yet" from "nothing at all". */
  readonly loaded$ = this.loadedSubject.asObservable();
  /** Unread notifications only, for the toolbar bell. */
  readonly unreadNotifications$: Observable<SchoolNotification[]>;
  readonly unreadCount$: Observable<number>;

  constructor(private schoolNotificationService: SchoolNotificationService) {
    const unread$ = this.notifications$.pipe(map((notifications) => notifications.filter((n) => !n.read)));
    this.unreadNotifications$ = unread$.pipe(map((unread) => unread.slice(0, MAX_BELL_NOTIFICATIONS)));
    this.unreadCount$ = unread$.pipe(map((unread) => unread.length));
  }

  /** Loads the inbox now and then refreshes it periodically until the subscription is closed. */
  startPolling(): Subscription {
    this.refresh();
    return interval(NOTIFICATION_POLL_INTERVAL_MS).subscribe(() => this.refresh());
  }

  /** Re-reads the inbox. A failed read leaves the current list alone rather than blanking the page. */
  refresh(): void {
    this.schoolNotificationService
      .load()
      .pipe(catchError(() => of(null)))
      .subscribe((records) => {
        if (records) {
          const shown = records.filter(hasWording);
          // Anonymised notices are hidden, not gone: "mark all" and "clear" still reach them (final review, L8).
          this.hiddenCount = records.length - shown.length;
          this.notificationsSubject.next(shown.map(toNotification));
        }
        this.loadedSubject.next(true);
      });
  }

  markAsRead(id: string): void {
    if (!this.notificationsSubject.value.some((n) => n.id === id && !n.read)) {
      return;
    }
    this.applyLocally((n) => (n.id === id ? { ...n, read: true } : n));
    this.write(this.schoolNotificationService.markAsRead([id]));
  }

  markAllAsRead(): void {
    if (!this.notificationsSubject.value.some((n) => !n.read) && this.hiddenCount === 0) {
      return;
    }
    this.applyLocally((n) => ({ ...n, read: true }));
    this.write(this.schoolNotificationService.markAllAsRead());
  }

  /** Empties this user's inbox. Other users keep theirs and the records behind the notices stay. */
  clearAll(): void {
    if (this.notificationsSubject.value.length === 0 && this.hiddenCount === 0) {
      return;
    }
    this.notificationsSubject.next([]);
    this.write(this.schoolNotificationService.clearAll());
  }

  private applyLocally(change: (notification: SchoolNotification) => SchoolNotification): void {
    this.notificationsSubject.next(this.notificationsSubject.value.map(change));
  }

  /** Whether the write succeeded or not, the list is re-read: the server decides what is shown. */
  private write(call: Observable<void>): void {
    call.pipe(catchError(() => of(null)), tap(() => this.refresh())).subscribe();
  }
}

/**
 * A notice about a person whose data was anonymised (DIGI-1854/1855) loses its title and message on
 * the server. It is not shown, so it neither lists as an empty row nor counts as unread.
 */
function hasWording(record: SchoolNotificationRecord): boolean {
  return !!record.title || !!record.message;
}

function toNotification(record: SchoolNotificationRecord): SchoolNotification {
  return {
    id: record.id,
    kind: record.kind,
    title: record.title,
    message: record.message,
    date: record.createdDatetime ? convertZonedDateTimeToDate(record.createdDatetime) : undefined,
    read: record.read,
    link: linkFor(record),
    fragment: record.kind === 'PARENT_CANCELLATION' || record.kind === 'MIS_SYNC_FINISHED' ? undefined : record.referenceId ?? undefined,
  };
}

/** Which page answers this notice. The routes stay here; the backend only says what kind it is. */
function linkFor(record: SchoolNotificationRecord): string[] {
  switch (record.kind) {
    case 'PUPIL_LINK_REQUEST':
      return [PUPIL_LINK_REQUESTS_ROUTE];
    case 'ACCOUNT_DEACTIVATION_REQUEST':
      return [DEACTIVATION_REQUESTS_ROUTE];
    case 'MIS_SYNC_FINISHED':
      return [MIS_INTEGRATION_ROUTE];
    default:
      return record.targetAccountId ? ['/people/users/edit', record.targetAccountId] : ['/people/users'];
  }
}

/**
 * Splits notifications (already sorted newest first) into calendar inbox periods, in this order:
 * Today, Yesterday, This Week (Monday until the day before yesterday), Last Week (previous Monday to Sunday),
 * This Month (before last week), Last Month, This Year (before last month), Older (incl. undated).
 * Periods never overlap. A period whose calendar range is empty is left out, so there is no
 * "This Week" on a Monday or Tuesday, no "This Month" in the first days of a month and no
 * "This Year" in January or February.
 */
export function groupNotificationsByPeriod(notifications: SchoolNotification[], now: Date = new Date()): NotificationPeriodGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = daysBefore(startOfToday, 1);
  const startOfThisWeek = daysBefore(startOfToday, (startOfToday.getDay() + 6) % 7);
  const startOfLastWeek = daysBefore(startOfThisWeek, 7);
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  // Each period starts at `from` and ends where the previous (more recent) period starts.
  const candidates: { key: NotificationPeriodKey; label: string; from: number }[] = [
    { key: 'today', label: 'Today', from: startOfToday.getTime() },
    { key: 'yesterday', label: 'Yesterday', from: startOfYesterday.getTime() },
    { key: 'thisWeek', label: 'This Week', from: startOfThisWeek.getTime() },
    { key: 'lastWeek', label: 'Last Week', from: startOfLastWeek.getTime() },
    { key: 'thisMonth', label: 'This Month', from: startOfThisMonth.getTime() },
    { key: 'lastMonth', label: 'Last Month', from: startOfLastMonth.getTime() },
    { key: 'thisYear', label: 'This Year', from: startOfYear.getTime() },
    { key: 'older', label: 'Older', from: Number.NEGATIVE_INFINITY },
  ];
  const periods: typeof candidates = [];
  for (const candidate of candidates) {
    const previous = periods[periods.length - 1];
    if (!previous || candidate.from < previous.from) {
      periods.push(candidate);
    }
  }
  const groups: NotificationPeriodGroup[] = periods.map(({ key, label }) => ({ key, label, notifications: [] }));

  for (const notification of notifications) {
    const time = notification.date?.getTime() ?? Number.NEGATIVE_INFINITY;
    const index = periods.findIndex((period) => time >= period.from);
    groups[index].notifications.push(notification);
  }
  return groups;
}

function daysBefore(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - days);
}
